export function isLoginPage(pathname: string) {
    if (pathname.startsWith("/public")) return true;
    return false;
}

/**
 * The OpenID Connect profile claims of the logged-in user, as `/api/user-data` returns them.
 *
 * This UI holds no token of its own - the server keeps it and the browser only carries the
 * session cookie - so these come from Heimdall's API rather than from the userinfo endpoint.
 */
export type UserProfile = {
    sub: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
    locale?: string;
    birthdate?: string;
    gender?: string;
};

/** Sends the browser to Heimdall's own login, returning to the current page afterwards. */
export function redirectToLogin(): void {
    const params = new URLSearchParams({ next: window.location.pathname + window.location.search });

    const loginHint = new URLSearchParams(window.location.search).get("login_hint");
    if (loginHint) params.set("login_hint", loginHint);

    window.location.href = `/auth/login?${params}`;
}

/**
 * Removes what the OIDC client used to keep in `localStorage` - the tokens among them.
 *
 * Kept for browsers that ran this UI before it stopped holding tokens, so that none are left behind.
 */
export function forgetLegacyOidcState(): void {
    try {
        for (const key of Object.keys(window.localStorage)) {
            if (key.startsWith("oidc.") || key === "beforeLoginUrl") window.localStorage.removeItem(key);
        }
    } catch (e) {
        console.debug("Could not clean up the legacy OIDC state", e);
    }
}

export type Permission = "system" | "client" | "user";

type Context = {
    data: Record<string, any>;
    name: string;
};

export type Role = {
    contexts: Context[];
    name: string;
    permissions: Permission[];
};

export class UserData {
    public readonly userId: number;
    public readonly accountPage: string;
    private _permissions: Permission[];
    public readonly profile: UserProfile;

    constructor(userId: number, accountPage: string, profile: UserProfile, permissions: Permission[]) {
        this.userId = userId;
        this.accountPage = accountPage;
        this._permissions = permissions;
        this.profile = profile;
    }

    hasPermission(permission: Permission): boolean {
        return this._permissions.includes(permission);
    }

    get name(): string {
        return this.profile.name;
    }
}
